import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CallV2, OutboundCallRequest, CallQueryParams, CallQueryResponse, StrategyWithVersions } from "@/lib/types";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CallActionsMenu } from "./CallActionsMenu";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import LocalTimeDisplay from "./LocalTimeDisplay";
import { CallFilters } from "./CallFilters";
import { 
  Loader2, 
  ChevronDown, 
  ChevronUp, 
  Copy,
  Clock,
  Phone,
  CheckCircle,
  XCircle,
  Voicemail
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface OrganizationCallListProps {
  organizationId: string;
  onCallCreated: boolean;
}

export function OrganizationCallList({
  organizationId,
  onCallCreated,
}: OrganizationCallListProps) {
  const [calls, setCalls] = useState<CallV2[]>([]);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [fetching, setFetching] = useState(false);
  const [response, setResponse] = useState<CallQueryResponse | null>(null);
  const [strategies, setStrategies] = useState<StrategyWithVersions[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL search params
  const getFiltersFromURL = (): CallQueryParams => {
    const params: CallQueryParams = {
      limit: parseInt(searchParams.get("limit") || "20"),
      offset: parseInt(searchParams.get("offset") || "0"),
      sort_by: (searchParams.get("sort_by") as "created_at" | "call_started_at" | "call_ended_at") || "created_at",
      sort_order: (searchParams.get("sort_order") as "asc" | "desc") || "desc",
    };

    // Add optional filters if they exist in URL
    const calleePhone = searchParams.get("callee_phone");
    if (calleePhone) params.callee_phone_number = calleePhone;

    const strategyId = searchParams.get("strategy_id");
    if (strategyId) params.strategy_id = strategyId;

    const status = searchParams.get("call_status");
    if (status) params.call_status = status as "pending" | "in_progress" | "completed" | "failed" | "voicemail";

    const startedAfter = searchParams.get("call_started_after");
    if (startedAfter) params.call_started_after = startedAfter;

    const startedBefore = searchParams.get("call_started_before");
    if (startedBefore) params.call_started_before = startedBefore;

    return params;
  };

  // Initialize filters from URL
  const [filters, setFilters] = useState<CallQueryParams>(getFiltersFromURL());

  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || 20)) + 1;
  const totalPages = response ? Math.ceil(response.pagination.total / (filters.limit || 20)) : 0;

  // Update URL params when filters change
  const updateURLParams = (newFilters: CallQueryParams) => {
    const params = new URLSearchParams();
    
    // Always include these params
    params.set("limit", newFilters.limit?.toString() || "20");
    params.set("offset", newFilters.offset?.toString() || "0");
    params.set("sort_by", newFilters.sort_by || "created_at");
    params.set("sort_order", newFilters.sort_order || "desc");
    
    // Only include optional params if they have values
    if (newFilters.callee_phone_number) {
      params.set("callee_phone", newFilters.callee_phone_number);
    }
    if (newFilters.strategy_id) {
      params.set("strategy_id", newFilters.strategy_id);
    }
    if (newFilters.call_status) {
      params.set("call_status", newFilters.call_status);
    }
    if (newFilters.call_started_after) {
      params.set("call_started_after", newFilters.call_started_after);
    }
    if (newFilters.call_started_before) {
      params.set("call_started_before", newFilters.call_started_before);
    }
    
    setSearchParams(params);
  };

  useEffect(() => {
    fetchCalls();
    fetchStrategies();
  }, [organizationId]);


  const fetchCalls = useCallback(() => {
    setFetching(true);
    api
      .getOrganizationCalls(organizationId, filters)
      .then((callResponse) => {
        setCalls(callResponse.calls);
        setResponse(callResponse);
      })
      .catch((error) => {
        console.error("Failed to fetch calls:", error);
        toast({
          title: "Error",
          description: "Failed to fetch calls. Please try again.",
          variant: "destructive",
        });
      })
      .finally(() => {
        setFetching(false);
      });
  }, [organizationId, filters]);

  const fetchStrategies = () => {
    api
      .getOrganizationStrategies(organizationId)
      .then((strategiesList) => {
        setStrategies(strategiesList);
      })
      .catch((error) => {
        console.error("Failed to fetch strategies:", error);
      });
  };

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  useEffect(() => {
    // Re-fetch calls when a new call is created
    fetchCalls();
  }, [onCallCreated]);

  const handleFiltersSubmit = (newFilters: CallQueryParams) => {
    setFilters(newFilters);
    updateURLParams(newFilters);
  };


  const handleStartCall = (e: React.MouseEvent, callId: string) => {
    e.stopPropagation();
    setLoading((prev) => ({ ...prev, [callId]: true }));

    const outboundCallRequest: OutboundCallRequest = {
      call_id: callId,
      enable_recording: true,
    };

    api
      .outboundCall(outboundCallRequest)
      .then(() => {
        toast({
          title: "Success",
          description: "Call started successfully.",
        });
        fetchCalls();
      })
      .catch((error) => {
        console.error("Failed to start call:", error);
        toast({
          title: "Error",
          description: "Failed to start call. Please try again.",
          variant: "destructive",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, [callId]: false }));
      });
  };

  const handleCloneCall = (e: React.MouseEvent, callId: string) => {
    e.stopPropagation();
    setLoading((prev) => ({ ...prev, [callId]: true }));

    api
      .cloneCall(callId)
      .then(() => {
        toast({
          title: "Success",
          description: "Call cloned successfully",
        });
        fetchCalls();
      })
      .catch((error) => {
        console.error("Failed to clone call:", error);
        toast({
          title: "Error",
          description: "Failed to clone call",
          variant: "destructive",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, [callId]: false }));
      });
  };

  const handleRowClick = (callId: string) => {
    const callIndex = calls.findIndex(call => call.id === callId);
    const callIds = calls.map(call => call.id);
    
    navigate(`/calls/${callId}`, {
      state: {
        callIds,
        currentIndex: callIndex,
        filters,
        totalCalls: response?.pagination.total || 0,
        returnUrl: `/calls?${searchParams.toString()}`
      }
    });
  };

  const truncateId = (id: string) => {
    if (!id) return "-";
    if (id.length <= 10) return id;
    return `${id.substring(0, 5)}...${id.substring(id.length - 5)}`;
  };

  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * (filters.limit || 20);
    const newFilters = { ...filters, offset: newOffset };
    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const getStatusIcon = (status: string) => {
    const iconClass = "h-5 w-5";
    const statusConfig = {
      pending: { 
        icon: <Clock className={`${iconClass} text-yellow-600`} />, 
        label: "Pending" 
      },
      in_progress: { 
        icon: <Phone className={`${iconClass} text-blue-600 animate-pulse`} />, 
        label: "In Progress" 
      },
      completed: { 
        icon: <CheckCircle className={`${iconClass} text-green-600`} />, 
        label: "Completed" 
      },
      failed: { 
        icon: <XCircle className={`${iconClass} text-red-600`} />, 
        label: "Failed" 
      },
      voicemail: { 
        icon: <Voicemail className={`${iconClass} text-purple-600`} />, 
        label: "Voicemail" 
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      icon: <Clock className={iconClass} />,
      label: status
    };

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center justify-center">
            {config.icon}
          </div>
        </TooltipTrigger>
        <TooltipContent>{config.label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters Toggle */}
      <Collapsible open={showFilters} onOpenChange={setShowFilters}>
        <div className="flex justify-between items-center mb-4">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm">
              {showFilters ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Hide Filters
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show Filters
                </>
              )}
            </Button>
          </CollapsibleTrigger>
          <div className="text-sm text-muted-foreground">
            {response && (
              <>
                Showing{" "}
                {response.pagination.total > 0
                  ? (filters.offset || 0) + 1
                  : 0}
                -{Math.min((filters.offset || 0) + (filters.limit || 20), response.pagination.total)} of{" "}
                {response.pagination.total} calls
              </>
            )}
          </div>
        </div>
        <CollapsibleContent>
          <CallFilters
            filters={filters}
            onFiltersSubmit={handleFiltersSubmit}
            strategies={strategies}
          />
        </CollapsibleContent>
      </Collapsible>


      {/* Loading State */}
      {fetching && !response && (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}

      {/* Table */}
      {!fetching || response ? (
        <Table className="text-sm [&_td]:py-2 [&_th]:py-2">
          <TableHeader>
            <TableRow>
              <TableHead>Strategy</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Ended</TableHead>
              <TableHead className="text-center">Action</TableHead>
              <TableHead className="w-8"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {calls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  {filters.callee_phone_number ? "No matching calls found." : "No calls found."}
                </TableCell>
              </TableRow>
            ) : (
              calls.map((call) => (
              <TableRow
                key={call.id}
                onClick={() => handleRowClick(call.id)}
                className="cursor-pointer hover:bg-gray-100 text-sm font-light h-10"
              >
                <TableCell className="font-medium">
                  {call.strategy_name || "-"}
                </TableCell>
                <TableCell>{call.callee_phone_number}</TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger>{truncateId(call.id)}</TooltipTrigger>
                    <TooltipContent>{call.id}</TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>{getStatusIcon(call.call_status)}</TableCell>
                <TableCell>
                  {call.call_started_at ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <LocalTimeDisplay
                          utcDateString={call.call_started_at}
                          formatPattern="M/d h:mm a"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <LocalTimeDisplay
                          utcDateString={call.call_started_at}
                          formatPattern="yyyy-MM-dd HH:mm:ss"
                        />
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {call.call_ended_at ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <LocalTimeDisplay
                          utcDateString={call.call_ended_at}
                          formatPattern="M/d h:mm a"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <LocalTimeDisplay
                          utcDateString={call.call_ended_at}
                          formatPattern="yyyy-MM-dd HH:mm:ss"
                        />
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {call.call_status === "pending" ? (
                    <Button
                      onClick={(e) => handleStartCall(e, call.id)}
                      disabled={loading[call.id]}
                      size="sm"
                    >
                      {loading[call.id] ? "..." : "Start"}
                    </Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={(e) => handleCloneCall(e, call.id)}
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          disabled={loading[call.id]}
                        >
                          {loading[call.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Clone Call</TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <CallActionsMenu
                    callId={call.id}
                    onCloneSuccess={fetchCalls}
                    disabled={loading[call.id]}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
          </TableBody>
        </Table>
      ) : null}

      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                className={
                  currentPage === 1
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            </PaginationItem>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Show first page, last page, current page, and pages around current
              let pageNum: number;

              if (totalPages <= 5) {
                // If 5 or fewer pages, show all page numbers
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                // Near the start
                if (i < 4) {
                  pageNum = i + 1;
                } else {
                  pageNum = totalPages;
                }
              } else if (currentPage >= totalPages - 2) {
                // Near the end
                if (i === 0) {
                  pageNum = 1;
                } else {
                  pageNum = totalPages - 4 + i;
                }
              } else {
                // In the middle
                if (i === 0) {
                  pageNum = 1;
                } else if (i === 4) {
                  pageNum = totalPages;
                } else {
                  pageNum = currentPage - 1 + (i - 1);
                }
              }

              // Add ellipsis where needed
              if (
                (i === 1 && pageNum !== 2) ||
                (i === 3 && pageNum !== totalPages - 1)
              ) {
                return (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => handlePageChange(pageNum)}
                    isActive={currentPage === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  handlePageChange(Math.min(totalPages, currentPage + 1))
                }
                className={
                  currentPage === totalPages
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
